import { useLocalObservable } from 'mobx-react-lite'
import { Container } from '@/utils/sparrowContainer'
import { useLocalStorage } from '@/utils/useSparrowLocalStorage'
import moment from 'moment'
import useDelivery, {
  DeliveryItem
} from '../hooks/useDelivery'
import type { UserInfo } from '../hooks/useUser'
import { globalStore } from '@/common'
import { uniq } from 'lodash-es'

type Store = {
  currProjects: DeliveryItem[]
  nextProjects: DeliveryItem[]
  currMyProjectMap: Record<string, DeliveryItem>
  nextMyProjectMap: Record<string, DeliveryItem>
  currMeetingIds: number[]
  nextMeetingIds: number[]
  currMeetingMap: Record<string, string[]>
  nextMeetingMap: Record<string, string[]>
}

type Props = Store & {
  init: () => void
}

function useWorkspaceContext(initState: Record<string, string>): Props {
  const [token, setToken] = useLocalStorage<string>({
    key: 'token'
  })
  const [userInfo, setUserInfo] = useLocalStorage<UserInfo>({
    key: 'userInfo'
  })
  const { getDeliveryList } = useDelivery()
  const store = useLocalObservable<Store>(() => ({
    currProjects: [],
    nextProjects: [],
    currMyProjectMap: {},
    nextMyProjectMap: {},
    currMeetingIds: [],
    nextMeetingIds: [],
    currMeetingMap: {},
    nextMeetingMap: {}
  }))

  const init = () => {
    if (!userInfo.teamList || !userInfo.teamList.length) {
      window.location.href = '/join'
      return
    }
    void globalStore.loadEnumConfigsByAll()

    const currDate = moment().isoWeekday(5).format('YYYY-MM-DD 00:00:00')
    const nextDate = moment()
      .add(1, 'weeks')
      .isoWeekday(5)
      .format('YYYY-MM-DD 00:00:00')

    Promise.all(
      [currDate, nextDate].map((finishDate: string) =>
        getDeliveryList({ finishDate })
      )
    ).then(([currProjects, nextProjects]) => {
      const currProjectInfo = getMyProjectMapFromProjects(
        currProjects || [],
        token
      )
      const nextProjectInfo = getMyProjectMapFromProjects(
        nextProjects || [],
        token
      )
      const currMeetingMap = getMeetingMapFromProjects(currProjects || [])
      const nextMeetingMap = getMeetingMapFromProjects(nextProjects || [])

      store.currProjects = currProjects || []
      store.nextProjects = nextProjects || []
      store.currMyProjectMap = currProjectInfo.myProjectMap
      store.nextMyProjectMap = nextProjectInfo.myProjectMap
      store.currMeetingIds = currProjectInfo.myMeetingIds
      store.nextMeetingIds = nextProjectInfo.myMeetingIds
      store.currMeetingMap = currMeetingMap
      store.nextMeetingMap = nextMeetingMap
    })
  }

  return Object.assign(store, {
    init
  })
}

function getMeetingMapFromProjects(res: DeliveryItem[]) {
  const meetingMap = {}

  res.forEach((item) => {
    if (!meetingMap[item.morningMeetingId]) {
      meetingMap[item.morningMeetingId] = []
    }

    meetingMap[item.morningMeetingId] = uniq(
      meetingMap[item.morningMeetingId].concat(
        item.userList.map((user) => user.user.name)
      )
    )
  })

  return meetingMap
}

function getMyProjectMapFromProjects(
  res: DeliveryItem[],
  currUserId: string
): { myProjectMap: Record<string, DeliveryItem>; myMeetingIds: number[] } {
  const myProjectMap: Record<string, DeliveryItem> = {}
  const myMeetingIds: number[] = []

  res.forEach((item) => {
    for (let i = 0, len = item.userList.length; i < len; i++) {
      if (item.userList[i].userId === currUserId) {
        myProjectMap[item.id] = item

        if (!myMeetingIds.includes(item.morningMeetingId)) {
          myMeetingIds.push(item.morningMeetingId)
        }

        break
      }
    }
  })

  return {
    myProjectMap,
    myMeetingIds
  }
}

export default Container.createContainer<Props, Record<string, string>>(
  useWorkspaceContext
)
